import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useTagsManage from '@/hooks/useTagsManage';
import { message, Modal, Input, Table, Pagination } from 'starry-sky-ui';
import type { ColumnType } from 'starry-sky-ui';
import type { TagItem } from '@/types/admin';
import './index.scss';


const Tags: React.FC = () => {
  const { data: allTags, loading, addTag, deleteTag, updateTag } = useTagsManage();

  // ---- 搜索 ----
  const [keyword, setKeyword] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleKeywordChange = useCallback((val: string) => {
    setKeyword(val);
  }, []);

  // ---- 客户端模糊搜索（id / value / label） ----
  const filteredTags = useMemo(() => {
    if (!keyword.trim()) return allTags;
    const kw = keyword.trim().toLowerCase();
    return allTags.filter((t) => {
      const idMatch = String(t.id ?? '').toLowerCase().includes(kw);
      const valueMatch = t.value.toLowerCase().includes(kw);
      const labelMatch = t.label.toLowerCase().includes(kw);
      return idMatch || valueMatch || labelMatch;
    });
  }, [allTags, keyword]);

  // ---- 分页 ----
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const total = filteredTags.length;
  const pagedTags = useMemo(
    () => filteredTags.slice((page - 1) * pageSize, page * pageSize),
    [filteredTags, page, pageSize]
  );

  const handlePageChange = useCallback((p: number, ps: number) => {
    setPage(p);
    setPageSize(ps);
  }, []);

  // 搜索时重置到第一页
  useEffect(() => {
    setPage(1);
  }, [keyword]);

  // ---- 新增/编辑弹窗 ----
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagItem | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const openAdd = useCallback(() => {
    setEditingTag(null);
    setEditValue('');
    setEditLabel('');
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((tag: TagItem) => {
    setEditingTag(tag);
    setEditValue(tag.value);
    setEditLabel(tag.label);
    setModalOpen(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    const value = editValue.trim();
    const label = editLabel.trim();
    if (!value || !label) {
      message.warning('请填写完整的标签信息');
      return;
    }

    setSubmitting(true);
    try {
      if (editingTag) {
        await updateTag({ id: editingTag.id!, value, label });
        message.success('标签更新成功！');
      } else {
        await addTag({ value, label });
        message.success('标签添加成功！');
      }
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  }, [editValue, editLabel, editingTag, addTag, updateTag]);

  // ---- 删除确认 ----
  const [deleteTarget, setDeleteTarget] = useState<TagItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteTag(deleteTarget.id!);
      message.success('标签删除成功！');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, deleteTag]);

  // ---- 列定义 ----
  const columns: ColumnType[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '值（value）',
      dataIndex: 'value',
      key: 'value',
      width: 180,
      render: (val: string) => <span className="tag-cell-value">{val}</span>,
    },
    {
      title: '名称（label）',
      dataIndex: 'label',
      key: 'label',
      width: 200,
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: unknown, record: TagItem) => (
        <div className="tag-cell-actions">
          <button
            className="tag-action-btn edit"
            onClick={() => openEdit(record)}
          >
            编辑
          </button>
          <button
            className="tag-action-btn delete"
            onClick={() => setDeleteTarget(record)}
          >
            删除
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="tags-container">
      <div className="tags-header">
        <h1 className="tags-title">标签管理</h1>
        <button className="tags-add-btn" onClick={openAdd}>+ 新增标签</button>
      </div>

      {/* 搜索栏 */}
      <div className="tags-filters">
        <Input
          placeholder="搜索 ID / 标签值 / 名称..."
          value={keyword}
          onChange={handleKeywordChange}
          allowClear
          size="middle"
          className="tags-search-input"
        />
        <span className="tags-count">共 {total} 个标签</span>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={pagedTags}
        rowKey={(record: TagItem) => record.id!.toString()}
        loading={loading}
        size="middle"
        bordered
        pagination={false}
        emptyText="暂无标签"
        onSortEnd={(sortResult) => { 
              console.log(sortResult);
            }}
      />

      {/* 分页 */}
      {total > 0 && (
        <div className="tags-pagination-wrap">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showTotal
            showSizeChanger
            pageSizeOptions={[10, 20, 30, 50]}
            onChange={handlePageChange}
            
          />
        </div>
      )}

      {/* 新增/编辑弹窗 */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingTag ? '编辑标签' : '新增标签'}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        okText="确定"
        cancelText="取消"
      >
        <div className="tag-modal-body">
          <div className="tag-modal-field">
            <label className="tag-modal-label">标签值（value）</label>
            <Input
              value={editValue}
              onChange={(val) => setEditValue(val)}
              placeholder="例如: react"
            />
          </div>
          <div className="tag-modal-field">
            <label className="tag-modal-label">标签名（label）</label>
            <Input
              value={editLabel}
              onChange={(val) => setEditLabel(val)}
              placeholder="例如: React"
            />
          </div>
        </div>
      </Modal>

      {/* 删除确认弹窗 */}
      <Modal
        open={deleteTarget !== null}
        title="确认删除"
        onOk={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
        confirmLoading={deleteLoading}
        okText="删除"
        cancelText="取消"
      >
        <p>确定要删除标签「{deleteTarget?.label}」吗？</p>
      </Modal>
    </div>
  );
};

export default Tags;
